import type { Sound } from 'phaser';
import { GameObjects, Scene } from 'phaser';
import * as GameEngine from 'hearts-engine';

import Card, { CardEvent } from '@/GameUI/Sprites/Card';
import Button, { BtnEvent } from '@/GameUI/Components/Button';
import RoundScoreBoard, { RoundScoreBoardEvent } from '@/GameUI/Components/RoundScoreBoard';
import LayoutManager from '@/GameUI/Scenes/LayoutManager';
import { MAX_HEIGHT, MAX_WIDTH } from '@/utilities';
import { getResolution } from '@/GameUI/Constants';
import type { GameOptions } from '@/gameUI';
import MiniScore from '@/GameUI/Sprites/MiniScore';
import type { TFunction } from '@/GameUI/Definitions';
import TimerBar from '@/GameUI/Sprites/TimerBar';

function getPassDirectionName(passDirection: GameEngine.PassDirection): string {
	switch (passDirection) {
		case GameEngine.PassDirection.Left:
			return 'Left';
		case GameEngine.PassDirection.Opposite:
			return 'Across';
		case GameEngine.PassDirection.Right:
			return 'Right';
		case GameEngine.PassDirection.None:
			return 'None';
	}
}

const instructionsStyle: Phaser.Types.GameObjects.Text.TextStyle = {
	fontFamily: 'Arial',
	fontSize: 40,
	color: '#FFFFFF',
	resolution: getResolution(),
	wordWrap: {
		width: 350,
	},
	align: 'center',
};

const Sounds = {
	CardClickSound: 'click_lower_volumn',
	PointSound: 'whoop',
	AchievementSound: 'chime',
	WinSound: 'win',
	HeartBreakSound: 'snap',
} as const;

class GameScene extends Scene {
	width_Min: number = 800;
	width: number = 800;
	height_Min: number = 800;
	height: number = 800;
	gameManager: GameEngine.Manager;
	textbox: GameObjects.Text = null;
	cards: Card[] = [];
	continueButton: Button;
	roundScoreBoard: RoundScoreBoard;
	backButton: GameObjects.Image;
	isLoaded: boolean = false;
	background: GameObjects.TileSprite;
	menuBar: GameObjects.Graphics;
	timerId: number;
	instructions: GameObjects.Text;
	heartsBrokenIcon: GameObjects.Image;
	options: GameOptions;
	sounds: { [key: string]: Sound.BaseSound } = {};
	miniScores: MiniScore[] = [];
	timerBars: TimerBar[] = [];
	t: TFunction;
	pileMap: { [key: string]: string } = {};

	constructor(options: GameOptions) {
		super('scene-game');
		this.options = options;
		this.t = options.translate;
		this.gameManager = options.gameManager;

		//Main events to listen for and update the UI
		this.gameManager.on(GameEngine.GameEvent.StateChanged, (state) => {
			GameEngine.assumeType<GameEngine.GameState>(state);
			this.onStateChanged(state);
		});

		this.generatePileMap();
	}

	createDeck(baseCards: GameEngine.CardObject[]) {
		this.cards = [];

		for (const baseCard of baseCards) {
			const card = new Card(baseCard, this, 0, 0, true);
			card.visible = false;
			card.on(CardEvent.PRESSED, () => this.handleCardPressed(card));
			this.cards.push(card);
			this.add.existing(card);
		}
	}

	preload() {
		this.load.image('WoodBackground', 'assets/woodBackground.png');
		this.load.image('BackButton', 'assets/misc/BackButton.png');
		this.load.svg('HeartsBroken', 'assets/misc/broken_heart.svg', {
			width: 100,
			height: 100,
		});

		this.load.svg('miniHeart', 'assets/misc/hearts.svg', {
			width: 35,
			height: 35,
		});

		this.load.svg('miniSpade', 'assets/misc/spades.svg', {
			width: 35,
			height: 35,
		});
		this.load.atlas('Deck', 'assets/decks/MedievalDeck_SpriteSheet.png', 'assets/decks/MedievalDeck_SpriteSheet.json');
		this.load.atlas('Avatar', 'assets/avatar_sprites/avatars.png', 'assets/avatar_sprites/avatars.json');

		for (const value of Object.values(Sounds)) {
			this.load.audio(value, [`assets/Audio/${value}.wav`, `assets/Audio/${value}.mp3`]);
		}
	}

	create() {

		for (const sound of Object.values(Sounds)) {
			this.sounds[sound] = this.sound.add(sound);
		}

		this.background = this.add.tileSprite(0, 0, MAX_WIDTH, MAX_HEIGHT, 'WoodBackground').setOrigin(0, 0).setDepth(-1);

		this.continueButton = new Button(this.t('Continue'), 0xC03500, 420, 150, null, this, 0, 0);
		this.continueButton.setVisible(false);
		this.continueButton.on(BtnEvent.PRESSED, () => {
			this.handleFinishedPass();
		});
		this.add.existing(this.continueButton);

		this.roundScoreBoard = new RoundScoreBoard(this, this.gameManager.gameState.scoreThreshold, this.gameManager.gameState.players, this.t, this.getPlayerPositions());
		this.roundScoreBoard.setVisible(false);
		this.roundScoreBoard.setDepth(10000);
		this.roundScoreBoard.on(RoundScoreBoardEvent.CLOSE_BOARD, () => {
			this.handleRoundScoreBoardClose();
		});
		this.add.existing(this.roundScoreBoard);

		this.instructions = new GameObjects.Text(this, this.cameras.main.worldView.x + this.cameras.main.width / 2, 200, '', instructionsStyle);
		this.instructions.setOrigin(0.5);
		this.add.existing(this.instructions);

		this.heartsBrokenIcon = new GameObjects.Image(this, -100, -100, 'HeartsBroken');
		this.add.existing(this.heartsBrokenIcon);

		for (const player of this.gameManager.gameState.players) {
			const miniScore = new MiniScore(this, player.name, player.avatar);
			this.miniScores[player.position] = miniScore;
			this.add.existing(miniScore);

			const timerBar = new TimerBar(this, 20);
			this.timerBars[player.position] = timerBar;
			this.add.existing(timerBar);
		}

		this.createDeck(this.gameManager.gameState.deck);

		this.scale.on('resize', this.resize, this);
		this.input.on('pointerdown', this.nextAction, this);

		this.isLoaded = true;

		this.resize();

		this.onStateChanged();

		if (typeof window !== 'undefined') {
			window.addEventListener('keydown', (e) => {
				if (e.key === 'p') {
					this.handlePause();
				}
			});
		}

		LayoutManager.scene = this;
		this.updateLayout();
	}

	update() {

	}

	updateOptions(options: Partial<GameOptions>) {
		this.options = {
			...this.options,
			...options,
		};
	}

	playSound(soundKey: string) {
		if (this.options.audioEnabled) {
			this.sounds?.[soundKey]?.play?.();
		}
	}

	resize() {
		if (this.cameras && this.cameras.main && this.game && this.game.scale) {
			const camera = this.cameras.main;
			const gameWidth = this.game.scale.width;
			const gameHeight = this.game.scale.height;

			//resize scene to fit the screen to scale with the same ratio
			if (gameWidth > gameHeight) {
				const ratio = this.height_Min / gameHeight;
				this.width = gameWidth * ratio;
				this.height = this.height_Min;
			} else {
				const ratio = this.width_Min / gameWidth;
				this.width = this.width_Min;
				this.height = gameHeight * ratio;
			}

			//zoom camera to fit the scene
			const sceneWidth = this.width;
			const sceneHeight = this.height;
			const scaleX = gameWidth / sceneWidth;
			const scaleY = gameHeight / sceneHeight;
			const ration = Math.min(scaleX, scaleY);
			camera.setZoom(ration);
			camera.centerOn(sceneWidth / 2, sceneHeight / 2);

			if (this.options.debug) {
				console.log('resize');
			}

		}

		this.updateLayout();
	}

	handlePause() {
		if (!this.isLoaded) {
			return;
		}

		if (this.scene.isPaused()) {
			this.scene.resume();
		} else {
			this.scene.pause();
		}
	}

	updateSceneSize() {

	}

	updateLayout(gameState = this.gameManager.gameState) {
		if (!this.isLoaded) {
			return;
		}

		//transformCordinates to update all x and y for responsiveness

		LayoutManager.transformCordinates(this.width, this.height);

		LayoutManager.updateProperties(this.background, 'background');
		LayoutManager.updateProperties(this.roundScoreBoard, 'roundScoreBoard');
		LayoutManager.updateProperties(this.menuBar, 'menuBar');
		LayoutManager.updateProperties(this.backButton, 'backButton');
		LayoutManager.updateProperties(this.instructions, 'instructions');
		LayoutManager.updateProperties(this.continueButton, 'continueButton');

		if (!gameState) {
			return;
		}

		for (const player of gameState.players) {
			const { position } = player;
			const miniScore = this.miniScores[position];
			LayoutManager.updateProperties(miniScore, this.pileMap[`miniScore${position}`]);

			const timerBar = this.timerBars[position];
			LayoutManager.updateProperties(timerBar, this.pileMap[`timer${position}`]);
		}

		const playerHand = GameEngine.getPlayerHandName(this.options.localPlayer);

		this.continueButton.setVisible(gameState.deck.filter((c) => c.isSelected && c.pile === playerHand).length === 3);

		if (gameState.heartsBroken && this.heartsBrokenIcon.x < 0) {
			LayoutManager.updateProperties(this.heartsBrokenIcon, 'heartsBrokenIcon');
			this.playSound(Sounds.HeartBreakSound);
		}

		if (!gameState.heartsBroken && this.heartsBrokenIcon.x > 0) {
			LayoutManager.updateProperties(this.heartsBrokenIcon, 'heartsBrokenIconHidden');
		}

		//apply layouts to cardUIs based on pile
		if (LayoutManager.layoutCardPiles(this.cards, this, this.getPlayableCards())) {
			this.playSound(Sounds.CardClickSound);
		}

		//populate the score board
		if (gameState.roundStage === GameEngine.RoundStage.RoundEnd && !this.roundScoreBoard.visible) {
			this.playSound(Sounds.AchievementSound);
		}

		//game over
		if (gameState.roundStage === GameEngine.RoundStage.GameOver && !this.roundScoreBoard.visible) {
			this.playSound(Sounds.WinSound);
		}

		this.roundScoreBoard.setVisible(gameState.roundStage === GameEngine.RoundStage.RoundEnd || gameState.roundStage === GameEngine.RoundStage.GameOver);

		//log game state
		if (this.options.debug) {
			console.log('roundStage: ', gameState.roundStage, 'currentPlayer: ', gameState.currentPlayer);
		}

		this.triggerBots();
	}

	getPositionMap(): GameEngine.PositionMap {
		const gameState = this.gameManager.gameState;

		return GameEngine.getPositionMap(this.options.localPlayer, gameState.playerCount);
	}

	getPlayableCards() {
		const actions = this.gameManager.getValidActions(this.options.localPlayer);
		const playableCards = [];

		for (const action of actions) {
			switch (action.command) {
				case GameEngine.GameAction.Togglecard:
				case GameEngine.GameAction.PlayCard:
					GameEngine.assumeType<GameEngine.PlayCardData>(action.data);
					playableCards.push(action.data.card);
					break;

				case GameEngine.GameAction.PassCards:
					GameEngine.assumeType<GameEngine.PassCardsData>(action.data);

					for (const card of action.data.cards) {
						playableCards.push(card);
					}

					break;
			}
		}

		return playableCards;
	}

	handleStart() {
		if (this.options.debug) {
			console.info('starting new game');
		}

		this.handleCreateNewGame();

		//TODO: move bots to game core
		//trigger each player to tick
		this.triggerBots();

		this.updateLayout();
	}

	handleCreateNewGame() {
		this.gameManager.action(GameEngine.GameAction.NewGame);
		this.gameManager.action(GameEngine.GameAction.Deal);
		// this.gameManager.createNewGame();
		// this.gameManager.deal();

		//todo: should reset on its own when the data is reset
		this.roundScoreBoard?.reset?.();
	}

	nextAction() {

		switch (this.gameManager.gameState.roundStage) {
			case GameEngine.RoundStage.GameOver:
				// this.handleCreateNewGame();
				break;
			case GameEngine.RoundStage.TrickEnd:
			case GameEngine.RoundStage.RoundEnd:
			case GameEngine.RoundStage.PassEnd:
				this.gameManager.nextStage();
				break;
		}
	}

	triggerBots() {
		this.gameManager.tick();
	}

	//-----------game logic event---------------------------
	onStateChanged(gameState?: GameEngine.GameState) {

		if (!this.isLoaded) {
			return;
		}

		//---data bind game state to UI-----//

		if (!gameState) {
			gameState = this.gameManager.gameState;
		}

		if (!gameState) {
			return;
		}

		if (this.options.debug) {
			console.info('GameState is now', gameState);
		}

		//Repopulate player info
		this.updatePlayerInfos(gameState);

		const currentRound = gameState.rounds?.at?.(-1);

		// populate the score board
		if (gameState.roundStage === GameEngine.RoundStage.RoundEnd || gameState.roundStage === GameEngine.RoundStage.GameOver) {
			const totalRoundScore = {
				total: gameState.score,
				round: currentRound?.score,
			};
			this.roundScoreBoard.setScore(totalRoundScore);
		}

		//populate the game over
		if (gameState.roundStage === GameEngine.RoundStage.GameOver) {
			const score = this.gameManager.gameState.score;
			let currentWinner = 0;

			for (let x = 0; x < gameState.players.length; ++x) {
				if (score[x] < score[currentWinner]) {
					currentWinner = x;
				}
			}

			const winner = gameState.players[currentWinner];

			this.roundScoreBoard.setInstructions(`${winner.name} ${this.t('wins')}!`);
			this.roundScoreBoard.setButtonText(this.t('Play Again'));
		}

		//populate the score board
		const currentScore = GameEngine.calculateSuitPoints(gameState.deck);

		for (let x = 0; x < this.miniScores.length; ++x) {
			const miniScore = this.miniScores[x];
			miniScore.setHearts(currentScore.hearts?.[x] ?? 0);
			miniScore.setHasQueen(currentScore.queen === x);
		}

		//populate card UIs with card model data
		for (const card of gameState.deck) {
			const cardUI = this.cards.find((ci) => ci.id === `${card.rank}${card.suit}`);

			if (!cardUI) {
				continue;
			}

			//If a card is selected to pass, put it back in the player's hand that passed it so we can properly move it to the "selected" position
			const decoded = GameEngine.decodePile(card.pile);

			if (!decoded) {
				continue;
			}

			const { player } = decoded;
			cardUI.isSelected = card.isSelected || card.pile.indexOf('pass') > -1;

			if (typeof this.pileMap[card.pile] === 'undefined' && this.options.debug) {
				console.warn('Missing pilemap entry for', card.pile);
			}

			cardUI.pile = this.pileMap[cardUI.isSelected ? GameEngine.getPlayerHandName(player) : card.pile] ?? card.pile;
			cardUI.pilez = card.order;
		}

		this.updateLayout();
		this.updateInstructions();

		if (gameState.roundStage === GameEngine.RoundStage.PassEnd &&
			(this.options.allBots || GameEngine.getPassDirectionFromGameState(gameState) !== GameEngine.PassDirection.None)) {
			this.nextAction();
		}
	}

	//-----------game UI events---------------------------//

	private handleFinishedPass() {
		if (this.gameManager.handleFinishPass(this.options.localPlayer)) {

			//Unselect all cards
			for (const card of this.cards) {
				card.isSelected = false;
			}

			this.nextAction();

		} else if (this.options.debug) {
			console.info('rules say we cant pass those cards');
		}
	}

	private handleCardPressed(card: Card) {

		const currentPlayer = this.options.localPlayer;
		const gameManager = this.gameManager;
		const playerHandName = GameEngine.getPlayerHandName(currentPlayer);

		if (this.options.debug) {
			console.log('handle card pressed', card, card.pile, playerHandName, currentPlayer);
		}

		if (card.pile !== this.pileMap[playerHandName]) {
			return;
		}

		if (gameManager.handleCardSelection(card.id, currentPlayer)) {
			if (this.options.debug) {
				console.info('gamestate is now', gameManager.gameState);
			}
		} else {
			if (this.options.debug) {
				console.warn('invalid card play action', card.id, currentPlayer, gameManager.gameState);
			}
		}
	}

	private handleRoundScoreBoardClose() {
		if (this.gameManager.gameState.roundStage === GameEngine.RoundStage.GameOver) {
			this.handleCreateNewGame();
			return;
		}

		if (!this.gameManager.nextStage()) {
			if (this.options.debug) {
				console.warn('invalid next stage action', this.gameManager);
			}
		}

	}

	private updateInstructions() {
		if (!this.instructions) {
			return;
		}

		this.instructions.setText('');

		const passDirection = GameEngine.getPassDirectionFromGameState(this.gameManager.gameState);

		switch (this.gameManager.gameState.roundStage) {
			case GameEngine.RoundStage.Pass:
				this.instructions.setText(`${this.t('Select 3 cards to pass')} ${this.t(getPassDirectionName(passDirection).toLowerCase())}`);
				break;

			case GameEngine.RoundStage.PassEnd:
				if (passDirection === GameEngine.PassDirection.None) {
					this.instructions.setText(this.t('There is no passing this round'));
					this.continueButton.setVisible(true);
				}

				break;
			default:
				this.continueButton.setVisible(false);
				break;
		}
	}

	private updatePlayerInfos(gameState: GameEngine.GameState = this.gameManager.gameState) {
		const players = gameState.players;

		for (const player of players) {
			const miniScore = this.miniScores[player.position];
			const playerTotalScore = gameState.score[player.position] ?? 0;
			const name = `${player.name} - ${playerTotalScore}`;
			const avatar = player.avatar;
			if (miniScore) { //shouldn't happen, but just in case
				miniScore.setPlayerName(name);
				miniScore.setAvatar(avatar);
			}

			const timer = this.timerBars[player.position];

			if (timer) {
				timer.value = player.timeLeft - 2;
			}

		}

		this.roundScoreBoard.setPlayers(gameState.players);
	}

	private getPlayerPositions() {
		const playerCount = this.gameManager.gameState.playerCount;
		const localPlayer = this.options.localPlayer;
		return GameEngine.getPlayerPositions(localPlayer, playerCount);
	}

	//TODO this probably shouldn't be here
	private generatePileMap() {
		const playerCount = this.gameManager.gameState.playerCount;
		const mapped = this.getPlayerPositions();

		//For each player, get their pile names
		for (let x = 0; x < playerCount; ++x) {
			const handName = GameEngine.getPlayerHandName(x);
			const passName = GameEngine.getPlayerPassHandName(x);
			const scoreName = GameEngine.getScorePileName(x);
			const trickName = GameEngine.getTrickPileName(x);
			this.pileMap[handName] = `hand${mapped[x]}`;
			this.pileMap[passName] = `hand${mapped[x]}`;
			this.pileMap[scoreName] = `${GameEngine.WINNINGS_PILE_NAME_PREFIX}${mapped[x]}`;
			this.pileMap[trickName] = `trick${mapped[x]}`;
			this.pileMap[`miniScore${x}`] = `miniScore${mapped[x]}`;
			this.pileMap[`timer${x}`] = `timer${mapped[x]}`;
		}

	}
}

export default GameScene;
